import { Component, HostBinding, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { modeNev, modeFavicon } from '../../mode-nevek';
import { KeretService } from '../../keret.service';
import { Subscription } from 'rxjs';
import { NaploService, NaploAktiv } from '../naplo.service';
import { NaploMenuService, LathatoFomenu } from '../naplo-menu.service';
import { NaploMenupont } from '../naplo-menu';

// NAPLO – a Könyvelés burka. Fejléc: márka, FŐMENÜ (Törzsadatok, Könyvelés, Naplók, …), az
// aktív ügyfél a könyvelési móddal és az évvel. A főmenüre kattintva az almenük csempékként
// nyílnak le (naplo-menu.ts). Ügyfél nélkül a főmenü nem jelenik meg: csak az ügyfélválasztó.
@Component({
  selector: 'app-naplo-layout',
  templateUrl: './naplo-layout.component.html',
  styleUrls: ['./naplo-layout.component.scss'],
})
export class NaploLayoutComponent implements OnInit, OnDestroy {
  // A fejlécben az alrendszer ÁTNEVEZHETŐ megjelenítési neve (mode-nevek.ts), nem bedrótozott szöveg.
  alrendszerNev = modeNev('NAPLO');
  aktiv: NaploAktiv = { intezmeny: '', ugyfelId: null, ugyfelNev: null, ev: new Date().getFullYear(), konyveles_mod: 'egyszeres' };
  // Kettős könyvelésű ügyfélnél az egész NAPLO-felület kékre vált (a téma CSS-változókon át).
  @HostBinding('class.tema-kettos') get temaKettos() { return this.aktiv.konyveles_mod === 'kettos'; }
  private sub?: Subscription;
  // A hosszú (háttérben futó) lekérések állapota. A BUROKBAN van, nem az egyes oldalakon, hogy
  // elnavigálva is látszódjon – ez az egész aszinkronizálás lényege.
  feladatok: any[] = [];
  private feladatSub?: Subscription;

  constructor(public router: Router, private naplo: NaploService, private menu: NaploMenuService,
              private keret: KeretService) {}

  /** A modul valódi ikonja (appicons/ → assets/icon/mode/), mint a böngészőfülön és az appon. */
  modIkon = modeFavicon('NAPLO');
  /** Fókusz téma: bal sáv nélkül, profil-lenyílóval (modul-tema.ts). */
  get fokusz(): boolean { return this.keret.a?.tema() === 'fokusz'; }

  // ── FŐMENÜ ──
  menuk: LathatoFomenu[] = [];
  nyitottMenu: string | null = null;
  aktivPont: string | null = null;
  private menuSub?: Subscription;
  private pontSub?: Subscription;
  get aktivFomenu(): string | null { return this.menu.aktivFomenu(); }
  get lenyiloPontok(): NaploMenupont[] { return this.menuk.find((x) => x.f.kulcs === this.nyitottMenu)?.pontok || []; }

  /*  NYITATLAN ÉV: a fejléc menüje is zárva. A könyvelés a nyitó egyenlegekkel kezdődik, addig a
   *  többi képernyő féligazságot mutatna – csak a nyitás és az ügyfélváltás marad elérhető. */
  get nyitasKell(): boolean { return this.naplo.nyitasKell; }
  tiltott(p: NaploMenupont): boolean {
    return this.nyitasKell && !this.naplo.nyitasKivetel((p as any).ut, (p as any).query);
  }

  menuValt(kulcs: string, ev?: Event) {
    ev?.stopPropagation();
    this.nyitottMenu = this.nyitottMenu === kulcs ? null : kulcs;
  }
  pontNyit(p: NaploMenupont) { this.nyitottMenu = null; this.menu.nyit(p); }

  /*  A saját címsor nélküli oldalak címe (a régi fejléc az oldal nevét is kiírta). A cím a
   *  főmenü-pontból jön, így a menüvel mindig egyezik. */
  /*  Akiknek SAJÁT címsoruk van (n-oldalfej a saját sablonjukban), azoknak a burok nem ír ki
   *  még egyet – különben két fejléc látszik egymás alatt. */
  private static readonly SAJAT_CIMES = new Set(['fomenu', 'ugyfelek', 'naplok', 'konyveles', 'kettos', 'torzsek', 'bank', 'kontir',
    'tarsashaz', 'fiokok', 'email-elkapo', 'opg', 'bevallasok', 'bevallas', 'nav-kapcsolat']);
  get autoCim(): { cim: string; fomenu: string; leiras: string } | null {
    const szakasz = (this.router.url.split('?')[0].split('/')[2] || '');
    if (!szakasz || NaploLayoutComponent.SAJAT_CIMES.has(szakasz) || !this.aktiv.ugyfelId) return null;
    for (const x of this.menuk) {
      const p = x.pontok.find((pp) => pp.kulcs === this.aktivPont);
      if (p) return { cim: p.cim, fomenu: x.f.cim, leiras: p.leiras };
    }
    const tartalek: Record<string, string> = { naplofokonyv: 'Naplófőkönyv', listak: 'Listák és ÁFA' };
    return tartalek[szakasz] ? { cim: tartalek[szakasz], fomenu: 'Naplók', leiras: '' } : null;
  }
  fomenuOldal() { this.nyitottMenu = null; this.router.navigateByUrl(this.aktiv.ugyfelId ? '/naplo/fomenu' : '/naplo/ugyfelek'); }
  ugyfelValaszto() { this.nyitottMenu = null; this.router.navigateByUrl('/naplo/ugyfelek'); }

  /*  Gyorsbillentyűk, ahogy a könyvelőprogramokban megszokott: Alt (Macen ⌥ Option) + a főmenü
   *  kezdőbetűje nyitja a főmenüt, Esc bezárja. (A kezdőbetűk egyediek: T, K, N, B, H, S.)
   *
   *  A betűt a FIZIKAI billentyűből (`e.code`: KeyT…) vesszük, nem az `e.key`-ből: Macen az
   *  Option + betű speciális karaktert ad (⌥T → „†", ⌥K → „˚"), így az `e.key` sosem egyezett –
   *  ezért nem működtek ott a gyorsbillentyűk. A kiosztás (QWERTZ/QWERTY) itt nem számít: a
   *  fenti betűk mindkettőben ugyanott vannak. */
  readonly modBill = /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent) ? '⌥' : 'Alt+';
  @HostListener('window:keydown', ['$event'])
  billentyu(e: KeyboardEvent) {
    if (e.key === 'Escape' && this.nyitottMenu) { this.nyitottMenu = null; return; }
    if (!e.altKey || e.ctrlKey || e.metaKey || !this.aktiv.ugyfelId) return;
    const betu = (/^Key([A-Z])$/.exec(e.code || '')?.[1] || String(e.key || '')).toLowerCase();
    const x = this.menuk.find((m) => m.f.cim[0].toLowerCase() === betu);
    if (x) { e.preventDefault(); this.nyitottMenu = x.f.kulcs; }
  }

  get futoFeladatok() { return this.feladatok.filter((f) => f.allapot === 'fut'); }
  get keszFeladatok() { return this.feladatok.filter((f) => f.allapot !== 'fut'); }
  szazalek(f: any): number | null {
    if (!f?.osszes) return null;                      // ismeretlen összmennyiség → határozatlan kör
    return Math.min(1, Math.max(0, (f.halad || 0) / f.osszes));
  }
  // A jelző EGY állapotot mutat: ha bármi fut, az a mérvadó; különben a legutóbbi eredmény.
  jelzoAllapot(): 'fut' | 'hiba' | 'kesz' | 'idle' {
    if (this.futoFeladatok.length) return 'fut';
    if (this.keszFeladatok.some((f) => f.allapot === 'hiba')) return 'hiba';
    if (this.keszFeladatok.length) return 'kesz';
    return 'idle';
  }
  // Tétlen állapotban NEM üres karika (az egy megszakadt haladásjelzőnek látszik), hanem
  // szinkron-ikon: „ide futnak be a háttérlekérések, most nincs dolguk".
  jelzoIkon(): string {
    const a = this.jelzoAllapot();
    return a === 'hiba' ? 'alert-circle' : (a === 'kesz' ? 'checkmark-circle' : 'sync-outline');
  }
  // Több párhuzamos feladatnál a legkevésbé előrehaladott a mérvadó (az fejeződik be utoljára).
  foFeladatSzazalek(): number | null {
    const sz = this.futoFeladatok.map((f) => this.szazalek(f));
    if (!sz.length || sz.some((x) => x === null)) return null;
    return Math.min(...(sz as number[]));
  }
  jelzoCim(): string {
    const fut = this.futoFeladatok;
    if (fut.length) return fut.map((f) => `${f.cimke}: ${f.uzenet || 'folyamatban'}`).join(' · ');
    const h = this.keszFeladatok.find((f) => f.allapot === 'hiba');
    if (h) return `${h.cimke}: ${h.hiba}`;
    if (this.keszFeladatok.length) return `${this.keszFeladatok[0].cimke}: kész`;
    return 'Nincs futó háttérfeladat';
  }

  // Kattintásra felugró panel a részletekkel. NEM ion-alert: annak a `message`-e sima szöveg,
  // a HTML-t (Angular/Ionic biztonsági alapbeállítás) nyersen kiírja – ezért saját sablon kell.
  feladatPopover = false;
  feladatEsemeny: any = null;
  feladatReszletek(ev: Event) { this.feladatEsemeny = ev; this.feladatPopover = true; }

  feladatAllapotSzoveg(f: any): string {
    if (f.allapot === 'fut') return f.uzenet || (f.osszes ? `${f.halad}/${f.osszes}` : 'folyamatban…');
    if (f.allapot === 'hiba') return f.hiba || 'ismeretlen hiba';
    if (f.allapot === 'megszakitva') return f.osszes ? `megszakítva (${f.halad}/${f.osszes})` : 'megszakítva';
    return f.halad ? `kész – ${f.halad} tétel` : 'kész';
  }
  /*  A futó lekérés leállítása. A kiszolgáló azonnal lezárja, így a következő pillanatban már
   *  indítható új lekérés ugyanarra az ügyfélre – nem kell a 10 perces elévülésre várni. */
  megszakitFut: string | null = null;
  async feladatMegszakit(f: any) {
    this.megszakitFut = f.id;
    try { await this.naplo.feladatMegszakit(f.id); }
    finally { this.megszakitFut = null; }
  }
  keszTorles() { this.keszFeladatok.forEach((f) => this.naplo.feladatElrejt(f.id)); }


  async ngOnInit() {
    await this.naplo.init();
    /*  ÜGYFÉL a saját könyvelését nézi: akinek a NAPLO-ban csak ügyfél-jogköre van (a könyvelő adta a
     *  portál-hozzáférést), annak a Könyvelés a portál – a könyvelői képernyőkhöz nincs joga. */
    try {
      const sz: any = await this.naplo.getAbs('/api/ugyfelportal/szerep');
      if (sz?.csak_ugyfel) { this.router.navigateByUrl('/ugyfelportal'); return; }
    } catch { /* nincs portál-végpont / nem dönthető el – a megszokott út */ }
    // NAPLO-jelölő a body-n: ezen át kapják meg a body-portálba kerülő overlay-ek (ion-modal/ion-alert)
    // a NAPLO-témaváltozókat (--naplo*, primary), mert a komponens :host-ját nem öröklik.
    document.body.classList.add('naplo-aktiv');
    this.sub = this.naplo.aktiv$.subscribe((a) => {
      this.aktiv = a;
      // A főmenü a könyvelési módtól és az ügyfél típusától függ (pl. társasház, kettős nézetek).
      this.menu.frissit();
      // a bal oldali app-menü (app-shell) a NAPLO-n kívül van → body-osztállyal váltjuk kékre kettősnél
      document.body.classList.toggle('naplo-tema-kettos', a.konyveles_mod === 'kettos');
      // Ügyfélváltáskor (és belépéskor) újralistázzuk a futó feladatokat: ha egy lekérés még megy,
      // az oldal újratöltése vagy másik gépről való belépés után is látszódjon.
      this.naplo.feladatFrissit().then(() => { if (this.naplo.futoFeladatok.length) this.naplo.pollIndit(); });
    });
    this.feladatSub = this.naplo.feladatok$.subscribe((f) => { this.feladatok = f || []; });
    this.menuSub = this.menu.lathato$.subscribe((m) => { this.menuk = m || []; });
    this.pontSub = this.menu.aktivPont$.subscribe((k) => { this.aktivPont = k; });
    // Oldalváltáskor a lenyitott menü becsukódik.
    this.router.events.subscribe(() => { if (this.nyitottMenu) this.nyitottMenu = null; });
  }
  ngOnDestroy() {
    this.naplo.kilepes();
    document.body.classList.remove('naplo-tema-kettos', 'naplo-aktiv');
    this.sub?.unsubscribe(); this.feladatSub?.unsubscribe(); this.menuSub?.unsubscribe(); this.pontSub?.unsubscribe();
  }

  open(url: string) { this.router.navigateByUrl(url); }
}

